import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ChatView } from '@lemoncloud/ssocio-chatbots-api';
import { Calendar, Loader2, MessageSquare, User } from 'lucide-react';

import { useDocument } from '@codes/chatbots';
import { formatDate } from '@codes/utils';

import { ChatInterface } from '../components';
import { useMyAgentChat } from '../hooks/useMyAgentChat';
import { transformDocumentData } from '../utils/document';

import type { DocumentData, DocumentResolver } from '../types/document';

interface ChatViewWithError extends ChatView {
    isError?: boolean;
}

export const MyAgentChatPage = () => {
    const { t } = useTranslation();
    const { id: agentId, chatId } = useParams<{ id: string; chatId: string }>();
    const navigate = useNavigate();

    // Document selection state
    const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
    const [documentResolver, setDocumentResolver] = useState<DocumentResolver | null>(null);

    // Data fetching
    const { data: selectedDocument, isLoading: isDocumentLoading } = useDocument(selectedDocId || '');
    const {
        agentData,
        displayMessages,
        input,
        setInput,
        addMessage,
        isLoading: isChatLoading,
        isWaitingResponse,
        isMessagesLoading,
        isLoadingMore,
        hasMore,
        loadMoreMessages,
    } = useMyAgentChat({
        agentId: agentId || '',
        chatId: chatId || '',
    });

    const agentName = agentData?.meta$?.nick ?? agentData?.id ?? t('agent.listItem.states.noName');
    const agentRole = agentData?.meta$?.role || t('agent.listItem.states.noRole');
    const chatCreatedAt = agentData?.createdAt ? formatDate(agentData.createdAt, 'YY.MM.DD HH:mm') : '';

    // Transform document data for DocumentModal
    const transformedDocumentData = useMemo(
        () => transformDocumentData(selectedDocument, selectedDocId),
        [selectedDocument, selectedDocId]
    );

    // Document resolution effect
    useEffect(() => {
        if (selectedDocument && documentResolver) {
            const documentData = transformDocumentData(selectedDocument, selectedDocId);
            if (documentData) {
                documentResolver(documentData);
                setDocumentResolver(null);
            }
        }
    }, [selectedDocument, documentResolver, selectedDocId]);

    // Promise-based document selection handler
    const handleDocumentSelect = useCallback(async (documentId: string): Promise<DocumentData> => {
        return new Promise<DocumentData>(resolve => {
            setDocumentResolver(() => resolve);
            setSelectedDocId(documentId);
        });
    }, []);

    const handleDocumentClose = useCallback(() => {
        setSelectedDocId(null);
    }, []);

    const handleSubmit = () => {
        if (input.trim() && !isWaitingResponse) {
            addMessage(input);
        }
    };

    const handleEdit = () => {
        toast({
            title: 'TODO',
            description: '수정 기능이 곧 추가될 예정입니다.',
            variant: 'default',
        });
    };

    const handleHistory = () => {
        toast({
            title: 'TODO',
            description: '수정이력 기능이 곧 추가될 예정입니다.',
            variant: 'default',
        });
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop } = e.currentTarget;
        if (scrollTop === 0 && hasMore && !isLoadingMore) {
            loadMoreMessages();
        }
    };

    if (isChatLoading) {
        return (
            <div className="bg-default h-full py-[42px] px-9 max-xs:px-6 w-full flex justify-center">
                <div className="w-full max-w-[1200px]">
                    <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                        <div className="text-center">
                            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
                            <p className="text-muted-foreground">{t('agent.catalog.loading.agentInfo')}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-background flex-1 min-h-0 py-[34px] px-9 max-md:px-4 max-md:py-[18px] w-full flex justify-center">
            <div className="w-full max-w-[1200px] flex flex-col gap-4">
                {/* Header with agent info */}
                {/* Agent Info Card */}
                <div className="bg-white dark:bg-[#1C1C1D] border border-chatbot-line rounded-lg p-4 max-md:p-3 shadow-sm flex flex-col">
                    <div className="flex items-start justify-between max-md:flex-col max-md:gap-3">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-point/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <MessageSquare className="w-5 h-5 text-point" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h1 className="text-lg font-semibold text-text truncate">{agentName}</h1>
                                    <p className="text-sm text-sub truncate">{agentRole}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 text-xs text-sub max-md:flex-wrap max-md:gap-2">
                                {agentData?.brain$?.name && (
                                    <div className="flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        <span className="truncate">{agentData.brain$.name}</span>
                                    </div>
                                )}
                                {agentData?.embedding && (
                                    <div className="flex items-center gap-1">
                                        <MessageSquare className="w-3 h-3" />
                                        <span className="truncate">{agentData.embedding}</span>
                                    </div>
                                )}
                                {chatCreatedAt && (
                                    <div className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        <span className="truncate">{chatCreatedAt}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/*<div className="flex items-center gap-2 max-md:w-full max-md:justify-end">*/}
                        {/*    <Button*/}
                        {/*        variant="outline"*/}
                        {/*        size="sm"*/}
                        {/*        onClick={handleEdit}*/}
                        {/*        className="text-xs h-8 max-md:flex-1"*/}
                        {/*    >*/}
                        {/*        {t('agent.catalog.actions.edit')}*/}
                        {/*    </Button>*/}
                        {/*    <Button*/}
                        {/*        variant="outline"*/}
                        {/*        size="sm"*/}
                        {/*        onClick={handleHistory}*/}
                        {/*        className="text-xs h-8 max-md:flex-1"*/}
                        {/*    >*/}
                        {/*        {t('agent.catalog.actions.editHistory')}*/}
                        {/*    </Button>*/}
                        {/*</div>*/}
                    </div>
                </div>

                {/* Chat Interface */}
                <ChatInterface
                    agentName={agentName}
                    messages={displayMessages as ChatViewWithError[]}
                    input={input}
                    setInput={setInput}
                    onSubmit={handleSubmit}
                    isWaitingResponse={isWaitingResponse}
                    isLoading={isMessagesLoading}
                    currentChat={{ id: chatId }}
                    selectedDocumentId={selectedDocId}
                    selectedDocumentData={transformedDocumentData}
                    isDocumentLoading={isDocumentLoading}
                    onDocumentSelect={handleDocumentSelect}
                    onDocumentClose={handleDocumentClose}
                    onEdit={handleEdit}
                    onHistory={handleHistory}
                    onScroll={handleScroll}
                    isLoadingMore={isLoadingMore}
                    hasMore={hasMore}
                    showEditActions={false}
                    showHeader={false}
                />
            </div>
        </div>
    );
};
